import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiDoubleLabel,
  MuiGrid,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTypography,
  OptionsDataType,
} from "../../../components";
import {
  CurrencyFormat,
  valueWithoutFormatInFloat,
} from "../../../components/textFieldCurrency/CurrencyUtils";
import { TextFieldCurrency } from "../../../components/textFieldCurrency/TextFieldCurrency";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  MAX_SCHEDULES_LIMIT,
} from "../../../helpers/constants";
import { COMPONENT_IDS } from "../../../helpers/constants/componentIds";
import { commonSiteCoreKeys } from "../../../helpers/constants/siteCoreKeys";
import {
  getAmountWithFormatByCurrencyName,
  getBillingScheduleExecutionStatus,
  getFormattedAmount,
  getSeriesIndexFromScheduleMilestone,
  isNotEmpty,
  isStatusExecuted,
  replaceHashWithValue,
} from "../../../helpers/utils";
import { useSiteCore } from "../../../hooks/useSiteCore";

import { useGridContainer } from "../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../hooks/useNavigationSiteCore";
import { AmountBadgeText } from "../../../styles/common.style";
import { STATUS_BADGE_TYPE, StatusBadge } from "../../statusBadge/StatusBadge";
import { BillingConfigurationDetailsHeader } from "../billingConfigurationDetailsHeader/BillingConfigurationDetailsHeader";
import { BillingConfigurationHeader } from "../billingConfigurationHeader/BillingConfigurationHeader";
import {
  BillingConfigurationDetailsFormKeyProps,
  BillingTypeMilestoneConfigureTableData,
  BillingTypeScheduleConfigureTableData,
  FieldType,
} from "../modelsAndSchemas/Models";
import { BillingConfigurationScheduleTypeWrapper } from "./BillingConfigurationScheduleType.style";

export interface BillingConfigurationScheduleTypeProps {
  pageId: string;
  currencyName: string;
  currencyFormat: CurrencyFormat;
  currencyId: string;
  addBillingConfigurationSchedule: () => void;
  onFocusAmountHandled: () => void;
  isSchedulePeriodMonthly: boolean;
  filteredResults:
    | BillingTypeScheduleConfigureTableData[]
    | BillingTypeMilestoneConfigureTableData[];
  onDeleteBillingConfigurationScheduleTableEntryAction: (
    row: BillingTypeScheduleConfigureTableData,
  ) => void;
  onDataAddedCallback: boolean;
  onDataDeletedCallback: boolean;
  updateBillingConfigurationSchedule: () => boolean;
  onEditScheduleEntry: (
    selectedScheduleEntryForEdit: BillingTypeScheduleConfigureTableData | null,
  ) => void;
  onEditCancel: () => void;
  billingConfigurationDetailsFormKeys: BillingConfigurationDetailsFormKeyProps;
  forceUpdate: () => void;
  isFromBillingInvoice?: boolean;
  isViewOnly?: boolean;
}

export const BillingConfigurationScheduleType: React.FC<
  BillingConfigurationScheduleTypeProps
> = ({
  pageId,
  currencyFormat,
  currencyId,
  currencyName,
  addBillingConfigurationSchedule,
  onFocusAmountHandled,
  isSchedulePeriodMonthly,
  filteredResults,
  onDeleteBillingConfigurationScheduleTableEntryAction,
  onDataAddedCallback,
  onDataDeletedCallback,
  updateBillingConfigurationSchedule,
  onEditScheduleEntry,
  onEditCancel,
  billingConfigurationDetailsFormKeys,
  forceUpdate,
  isFromBillingInvoice,
  isViewOnly = false,
}) => {
  const { gridSizeMedium: gridSize } = useGridContainer();
  const { control, getValues, setValue, clearErrors } = useFormContext();
  const {
    commonLabels,
    fetchDropdownValues,
    fetchValidationMessage,
    fetchValueByKeyOfDropdown,
    commonBillingConfigurationDetails,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    billingConfigurationAmount,
    feePercentage,
    schedule,
    scheduleFrequency,
    addScheduleSubHeading: addSchedule,
    editScheduleSubHeading: editSchedule,
    scheduleNumber,
    scheduleTableColumnHeading,
    amountTableColumnHeading,
    percentageTableColumnHeading,
    noSchedulesFoundMessage,
    scheduleFrequencyTableColumnHeading,
    actionTableColumnHeading: scheduleActionTableColumnHeading,
    totalAmount,
    totalPercentage,
    configurationDetailsScheduledBasedHeading,
    schedulePeriod,
    value: scheduleFeePaymentTypeLabel,
    statusTableColumnHeading: scheduleStatusTableColumnHeading,
  } = commonBillingConfigurationDetails;

  const [scheduleFrequencyDropdownData, setScheduleFrequencyDropdownData] =
    useState<OptionsDataType[]>([]);

  const [scheduleDropdownData, setScheduleDropdownData] = useState<
    OptionsDataType[]
  >([]);

  const defaultRawScheduleData = Object.fromEntries(
    Array.from({ length: MAX_SCHEDULES_LIMIT }, (_, i) => [i + 1, ""]),
  );

  const rawDataScheduleEntries = useRef<Record<number, string>>(
    defaultRawScheduleData,
  );
  const [selectedScheduleEntryForEdit, setSelectedScheduleEntryForEdit] =
    useState<BillingTypeScheduleConfigureTableData | null>();

  const {
    billingTypeIdKey,
    schedulePeriodKey,
    scheduleKey,
    feePercentageKey: feePercentageKeySchedule,
    amountKey: amountKeySchedule,
    amountWithFormatKey: amountWithFormatKeySchedule,
    scheduleFrequencyKey,
    feePaymentTypeKey: scheduleFeePaymentTypeKey,
    descriptionKey,
    statusKey: scheduleStatusKey = "",
    billingSchedulesKey,
  } = billingConfigurationDetailsFormKeys;

  const billingTypeIdValue = getValues(billingTypeIdKey);
  const scheduleFeePaymentTypeValue = getValues(scheduleFeePaymentTypeKey);
  const schedulePeriodValue = getValues(schedulePeriodKey);
  const billingSchedules = getValues(billingSchedulesKey);

  const isAllSchedulesExecuted = useMemo(
    () => getBillingScheduleExecutionStatus(billingSchedules).allExecuted,
    [billingSchedules],
  );

  const setNewValueSchedule = () => {
    if (isNotEmpty(scheduleDropdownData)) {
      setValue(scheduleKey, scheduleDropdownData[0].value);
    }
  };

  const checkScheduleDataEntries = () => {
    rawDataScheduleEntries.current = defaultRawScheduleData;
    filteredResults.forEach((item) => {
      const addScheduleNumber = getSeriesIndexFromScheduleMilestone(
        item.description,
      );

      rawDataScheduleEntries.current[addScheduleNumber] = item.frequency;
    });
  };

  const updateScheduleDataEntriesInEdit = (
    row: BillingTypeScheduleConfigureTableData,
  ) => {
    const addScheduleNumber = getSeriesIndexFromScheduleMilestone(
      row?.description ?? "",
    );
    rawDataScheduleEntries.current[addScheduleNumber] = "";
  };

  const resetScheduleDataEntriesInEdit = (
    row?: BillingTypeScheduleConfigureTableData | null,
  ) => {
    if (row) {
      const addScheduleNumber = getSeriesIndexFromScheduleMilestone(
        row?.description ?? "",
      );
      rawDataScheduleEntries.current[addScheduleNumber] = row.frequency;
      updateAllScheduleConfig();
    }
  };

  useEffect(() => {
    setValue(feePercentageKeySchedule, "");
    setValue(amountWithFormatKeySchedule, "");
  }, [scheduleFeePaymentTypeValue]);

  useEffect(() => {
    updateAllScheduleConfig();
  }, [onDataAddedCallback, onDataDeletedCallback, filteredResults]);

  const updateAllScheduleConfig = () => {
    checkScheduleDataEntries();
    updateScheduleDropdownData();
  };

  const updateScheduleDropdownData = () => {
    const remainingScheduleIndexes = Object.keys(rawDataScheduleEntries.current)
      .filter((key) => !rawDataScheduleEntries.current[Number(key)])
      .map(Number);

    const scheduleOptionData: OptionsDataType[] = remainingScheduleIndexes.map(
      (index) => {
        const labelValue = replaceHashWithValue(scheduleNumber, {
          NUMBER: `${index}`,
        });
        return { label: labelValue, value: labelValue };
      },
    );

    if (isNotEmpty(scheduleOptionData)) {
      setValue(scheduleKey, scheduleOptionData[0].value);
    }

    setScheduleDropdownData(scheduleOptionData);
  };

  const updateScheduleFrequencyDropdownData = () => {
    const scheduleFrequencyDropdownKey = isSchedulePeriodMonthly
      ? commonSiteCoreKeys.scheduleFrequencyMonthlyList
      : commonSiteCoreKeys.scheduleFrequencyWeeklyList;

    const frequencyOptions = fetchDropdownValues(
      commonSiteCoreKeys.billingConfigurationDetails,
      scheduleFrequencyDropdownKey,
    );

    setScheduleFrequencyDropdownData(frequencyOptions);
  };

  useEffect(() => {
    updateScheduleDropdownData();
    updateScheduleFrequencyDropdownData();
  }, [schedulePeriodValue]);

  useEffect(() => {
    setNewValueSchedule();
  }, [scheduleDropdownData]);

  const isValueTypePercentage = () => {
    const feePaymentTypeValueSchedule = getValues(scheduleFeePaymentTypeKey);
    return (
      feePaymentTypeValueSchedule ===
      BILLING_DETAILS_SITE_CORE_VALUE.valuePercentage
    );
  };

  const getScheduleTotalAmountOrPercentage = () => {
    let totalAmountOrPercentage = 0;
    filteredResults.forEach((item) => {
      totalAmountOrPercentage += isValueTypePercentage()
        ? Number(item.percentage)
        : valueWithoutFormatInFloat(item.amountWithFormat);
    });
    return isValueTypePercentage()
      ? totalAmountOrPercentage
      : getFormattedAmount(totalAmountOrPercentage, currencyFormat?.locale);
  };

  const reachedMaxScheduleLimit = () => {
    return (
      filteredResults.length === MAX_SCHEDULES_LIMIT || isAllSchedulesExecuted
    );
  };

  const resetScheduleValueOnEditFlowCancel = () => {
    setSelectedScheduleEntryForEdit(null);
    setValue(feePercentageKeySchedule, "");
    setValue(amountKeySchedule, "");
    setValue(amountWithFormatKeySchedule, "");
    setValue(scheduleFrequencyKey, "");
  };

  const clearScheduleErrorOnEditFlow = () => {
    clearErrors(scheduleKey);
    clearErrors(feePercentageKeySchedule);
    clearErrors(amountWithFormatKeySchedule);
    clearErrors(scheduleFrequencyKey);
  };

  const handleScheduleEdit = (row: BillingTypeScheduleConfigureTableData) => {
    clearScheduleErrorOnEditFlow();
    setSelectedScheduleEntryForEdit(row);
    updateScheduleDataEntriesInEdit(row);
    updateScheduleDropdownData();

    const amountWithFormatSchedule = getAmountWithFormatByCurrencyName(
      Number(row?.amount),
      currencyName,
    );
    setTimeout(() => {
      setValue(scheduleKey, row.description);
      updateScheduleFrequencyDropdownData();
      onEditScheduleEntry(row);
      setValue(feePercentageKeySchedule, row?.percentage);
      setValue(amountKeySchedule, row?.amount);
      setValue(amountWithFormatKeySchedule, amountWithFormatSchedule);
      setValue(scheduleFrequencyKey, row?.frequency);
    }, 500);
  };

  const handleScheduleEditCancelClick = () => {
    resetScheduleDataEntriesInEdit(selectedScheduleEntryForEdit);
    resetScheduleValueOnEditFlowCancel();
    clearScheduleErrorOnEditFlow();
    onEditCancel();
  };

  const handleScheduleUpdateClick = () => {
    if (updateBillingConfigurationSchedule()) {
      setSelectedScheduleEntryForEdit(null);
    }
  };

  const percentageOrAmountId = isValueTypePercentage()
    ? feePercentageKeySchedule
    : amountWithFormatKeySchedule;

  const percentageOrAmountLabel = isValueTypePercentage()
    ? percentageTableColumnHeading
    : amountTableColumnHeading;

  const columns: Column[] = [
    {
      id: scheduleKey,
      label: scheduleTableColumnHeading,
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{row.description}</MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: percentageOrAmountId,
      label: percentageOrAmountLabel,
      isSortVisible: false,
      render: (row: any) => {
        const amountWithFormatSchedule = getAmountWithFormatByCurrencyName(
          Number(row?.amount),
          currencyName,
        );
        return (
          <MuiBox>
            <MuiTypography variant="h3">
              {isValueTypePercentage()
                ? `${row.percentage}%`
                : `${currencyName} ${amountWithFormatSchedule}`}
            </MuiTypography>
          </MuiBox>
        );
      },
    },
    {
      id: scheduleFrequencyKey,
      label: scheduleFrequencyTableColumnHeading,

      isSortVisible: false,
      render: (row: any) => {
        let frequencyValue = "";

        frequencyValue = fetchValueByKeyOfDropdown(
          commonSiteCoreKeys.billingConfigurationDetails,
          isSchedulePeriodMonthly
            ? commonSiteCoreKeys.scheduleFrequencyMonthlyList
            : commonSiteCoreKeys.scheduleFrequencyWeeklyList,
          row.frequency,
        );

        return (
          <MuiBox>
            <MuiTypography variant="h3">{frequencyValue}</MuiTypography>
          </MuiBox>
        );
      },
    },
    ...(isFromBillingInvoice
      ? [
          {
            id: scheduleStatusKey,
            label: scheduleStatusTableColumnHeading,
            isSortVisible: false,
            render: (row: any) => {
              const scheduledStatus = isNotEmpty(row.statusId)
                ? row.statusId
                : STATUS_BADGE_TYPE.scheduled;
              return (
                <StatusBadge
                  status={scheduledStatus}
                  label={fetchValueByKeyOfDropdown(
                    commonSiteCoreKeys.billingConfigurationDetails,
                    commonSiteCoreKeys.billingStatusList,
                    scheduledStatus,
                  )}
                />
              );
            },
          },
        ]
      : []),
    ...(isViewOnly
      ? []
      : [
          {
            id: "action",
            label: scheduleActionTableColumnHeading,
            render: (row: any) => {
              if (row.description === selectedScheduleEntryForEdit?.description)
                return null;

              if (isFromBillingInvoice && isStatusExecuted(row.statusId)) {
                return null;
              }

              const lastScheduleRecord =
                filteredResults[filteredResults.length - 1].description;

              const menuItems = [
                {
                  onClick: () => handleScheduleEdit(row),
                  dataTestId: `edit-schedule-action`,
                  label: commonLabels.edit,
                },
              ];
              if (row.description === lastScheduleRecord) {
                menuItems.push({
                  onClick: () =>
                    onDeleteBillingConfigurationScheduleTableEntryAction(row),
                  dataTestId: `delete-schedule-action`,
                  label: commonLabels.delete,
                });
              }

              return <MuiOptionMenu menuItems={menuItems} />;
            },
          },
        ]),
  ];

  const showComponent =
    billingTypeIdValue === BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule;
  if (!showComponent) return null;

  const getFieldValue = (type: FieldType) => {
    const keyMap = {
      FeePaymentType: {
        value: getValues(scheduleFeePaymentTypeKey),
        list: commonSiteCoreKeys.valueList,
      },
      SchedulePeriod: {
        value: getValues(schedulePeriodKey),
        list: commonSiteCoreKeys.schedulePeriodList,
      },
    };
    const { value, list } = keyMap[type] || {};
    return value !== undefined && isNotEmpty(value)
      ? fetchValueByKeyOfDropdown(
          commonSiteCoreKeys.billingConfigurationDetails,
          list,
          value,
        )
      : "";
  };

  const renderTotalPercentageWithTable = () => {
    type BillingTypeScheduleConfigureTableDataKeys =
      keyof BillingTypeScheduleConfigureTableData;
    const descriptionRowKey: BillingTypeScheduleConfigureTableDataKeys =
      "description";
    return (
      <>
        <MuiBox className="billing-configuration-total-amount-percentage-container">
          <MuiTypography variant="h3Bold">
            {isValueTypePercentage() ? totalPercentage : totalAmount}
          </MuiTypography>
          <AmountBadgeText variant="h3Bold">
            {isValueTypePercentage()
              ? `${getScheduleTotalAmountOrPercentage()}%`
              : `${currencyName} ${getScheduleTotalAmountOrPercentage()}`}
          </AmountBadgeText>
        </MuiBox>
        <MuiBox className="billing-configuration-table">
          <MuiTable
            pageId={pageId}
            tableHeaderForm={[]}
            columns={columns}
            isFooterVisible={false}
            tableResults={[...filteredResults]}
            noDataInApi={false}
            noResultMessage={noSchedulesFoundMessage}
            priorityResults={[]}
            selectedId={selectedScheduleEntryForEdit?.description}
            selectedIdKey={descriptionRowKey}
          />
        </MuiBox>
      </>
    );
  };

  const renderViewOnlyLabel = (type: FieldType) => {
    return (
      <MuiDoubleLabel
        label={
          type === "SchedulePeriod"
            ? schedulePeriod
            : scheduleFeePaymentTypeLabel
        }
        replaceEmptyWithDash
        value={getFieldValue(type)}
      />
    );
  };

  const renderSchedulePeriodDropdownField = () => {
    return (
      <FormController control={control} name={schedulePeriodKey}>
        <MuiSelect
          data-testId={`${pageId}-${schedulePeriodKey}`}
          label={schedulePeriod}
          options={fetchDropdownValues(
            commonSiteCoreKeys.billingConfigurationDetails,
            commonSiteCoreKeys.schedulePeriodList,
          )}
          disabled={filteredResults.length > 0 || isAllSchedulesExecuted}
          onChange={forceUpdate}
          defaultValue={""}
          visibleDefaultSelectOption={true}
          isMandatory
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    );
  };

  const renderFeePaymentTypeDropdownField = () => {
    return (
      <FormController
        key={scheduleFeePaymentTypeValue}
        control={control}
        name={scheduleFeePaymentTypeKey}
      >
        <MuiSelect
          data-testId={`${pageId}-${scheduleFeePaymentTypeKey}`}
          label={scheduleFeePaymentTypeLabel}
          options={fetchDropdownValues(
            commonSiteCoreKeys.billingConfigurationDetails,
            commonSiteCoreKeys.valueList,
          )}
          disabled={filteredResults.length > 0 || isAllSchedulesExecuted}
          onChange={forceUpdate}
          defaultValue={""}
          visibleDefaultSelectOption={true}
          isMandatory
          placeholder={commonLabels.selectPlaceHolder}
          requiredARIALabel={commonARIALabel.required}
        />
      </FormController>
    );
  };

  const fieldConfigs = [
    {
      key: "SchedulePeriod",
      renderDropdown: renderSchedulePeriodDropdownField,
    },
    {
      key: "FeePaymentType",
      renderDropdown: renderFeePaymentTypeDropdownField,
    },
  ];

  return (
    <BillingConfigurationScheduleTypeWrapper>
      <MuiBox className="dashed-line" />
      <MuiBox className="config-header">
        <BillingConfigurationDetailsHeader
          header={configurationDetailsScheduledBasedHeading}
          showBlueBackground={!isFromBillingInvoice}
        />
      </MuiBox>
      <MuiBox
        className={`billing-configuration-top ${isViewOnly ? "margin-bottom" : ""}`}
      >
        <MuiGrid container spacing={2}>
          {fieldConfigs.map(({ key, renderDropdown }) => (
            <MuiGrid size={gridSize} key={key}>
              {isViewOnly
                ? renderViewOnlyLabel(key as FieldType)
                : renderDropdown()}
            </MuiGrid>
          ))}
        </MuiGrid>
      </MuiBox>
      {isViewOnly && renderTotalPercentageWithTable()}
      {!isViewOnly && schedulePeriodValue && scheduleFeePaymentTypeValue ? (
        <>
          <BillingConfigurationHeader
            header={selectedScheduleEntryForEdit ? editSchedule : addSchedule}
          />
          <MuiBox className="billing-configuration-middle">
            <MuiGrid container spacing={2}>
              <MuiGrid size={gridSize}>
                <FormController control={control} name={scheduleKey}>
                  <MuiSelect
                    data-testId={`${pageId}-${scheduleKey}`}
                    label={schedule}
                    options={scheduleDropdownData}
                    visibleDefaultSelectOption={true}
                    isMandatory
                    IconComponent={undefined}
                    disabled
                    placeholder={commonLabels.selectPlaceHolder}
                    onChange={updateScheduleFrequencyDropdownData}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
              {scheduleFeePaymentTypeValue ===
              BILLING_DETAILS_SITE_CORE_VALUE.valuePercentage ? (
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={feePercentageKeySchedule}
                  >
                    <TextFieldCurrency
                      isMandatory
                      id={`${pageId}-${feePercentageKeySchedule}`}
                      formId={feePercentageKeySchedule}
                      locale={currencyFormat?.locale}
                      useGrouping={false}
                      isCurrencyChanged={currencyId}
                      disabled={isAllSchedulesExecuted}
                      zeroValidationMessage={fetchValidationMessage(
                        commonSiteCoreKeys.greaterThanZeroFieldValidator,
                      )}
                      formatErrorMessage={fetchValidationMessage(
                        commonSiteCoreKeys.alphabetNotAllowedValidator,
                      )}
                      name={feePercentageKeySchedule}
                      label={feePercentage}
                      placeholder={commonLabels.inputPlaceHolder}
                      getFinalValues={(value, _valueNonFormatted) => {
                        setValue(`${feePercentageKeySchedule}`, value);
                      }}
                      percentRangeErrorMessage={fetchValidationMessage(
                        commonSiteCoreKeys.pleaseEnterNumberBetween1To100,
                      )}
                      allowDecimal={false}
                      isForPercentageUsage
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
              ) : (
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={amountWithFormatKeySchedule}
                  >
                    <TextFieldCurrency
                      isMandatory
                      id={`${pageId}-${amountWithFormatKeySchedule}`}
                      formId={amountWithFormatKeySchedule}
                      locale={currencyFormat?.locale}
                      isCurrencyChanged={currencyId}
                      disabled={isAllSchedulesExecuted}
                      zeroValidationMessage={fetchValidationMessage(
                        commonSiteCoreKeys.greaterThanZeroFieldValidator,
                      )}
                      formatErrorMessage={fetchValidationMessage(
                        commonSiteCoreKeys.alphabetNotAllowedValidator,
                      )}
                      name={amountWithFormatKeySchedule}
                      label={`${billingConfigurationAmount} (${currencyName})`}
                      placeholder={commonLabels.inputPlaceHolder}
                      getFinalValues={(value, valueNonFormatted) => {
                        setValue(amountKeySchedule, valueNonFormatted);
                        setValue(amountWithFormatKeySchedule, value);
                      }}
                      onFocusCalled={onFocusAmountHandled}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
              )}
              <MuiGrid size={gridSize}>
                <FormController control={control} name={scheduleFrequencyKey}>
                  <MuiSelect
                    data-testId={`${pageId}-${scheduleFrequencyKey}`}
                    label={scheduleFrequency}
                    options={scheduleFrequencyDropdownData}
                    defaultValue={""}
                    visibleDefaultSelectOption={true}
                    isMandatory
                    disabled={isAllSchedulesExecuted}
                    placeholder={commonLabels.selectPlaceHolder}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>

            <MuiBox className="billing-configuration-add-button">
              {selectedScheduleEntryForEdit ? (
                <>
                  <MuiButtonSecondary
                    id={COMPONENT_IDS.CANCEL_BUTTON}
                    className="bottom-button-clear"
                    onClick={handleScheduleEditCancelClick}
                  >
                    {commonLabels.cancel}
                  </MuiButtonSecondary>
                  <MuiButtonPrimary
                    id={COMPONENT_IDS.UPDATE_BUTTON}
                    className="bottom-button-continue"
                    onClick={handleScheduleUpdateClick}
                  >
                    {commonLabels.update}
                  </MuiButtonPrimary>
                </>
              ) : (
                <MuiButtonSecondary
                  onClick={addBillingConfigurationSchedule}
                  data-testId={COMPONENT_IDS.BILLING_CONFIGURATION_ADD_BUTTON}
                  disabled={reachedMaxScheduleLimit()}
                >
                  {commonLabels.add}
                </MuiButtonSecondary>
              )}
            </MuiBox>
          </MuiBox>

          {renderTotalPercentageWithTable()}
        </>
      ) : null}
    </BillingConfigurationScheduleTypeWrapper>
  );
};
